import { Controller } from '@hotwired/stimulus'

// Autocomplete search for financial assets in cotizaciones pages.
// Fetches results from the autocompletar/cotizaciones endpoint and
// navigates to the selected asset's ficha page.
//
// Usage:
//   div[data-controller="asset-search" data-asset-search-url-value="/opinables/autocompletar/cotizaciones"]
//     input[data-asset-search-target="input" data-action="input->asset-search#search keydown->asset-search#keydown"]
//     div[data-asset-search-target="results"]

// En el listado de cotizaciones este controlador convive con `cotizaciones-search`
// sobre el mismo campo, y aquel repinta el turbo-frame `content` al teclear: cada
// respuesta destruye y recrea este controlador con el desplegable vacío. El último
// resultado se guarda a nivel de módulo (no en la instancia) para volver a pintarlo
// tras el swap; sin esto, las sugerencias parpadeaban y desaparecían con cada
// pulsación. En el resto de páginas no hay swap y esto no llega a usarse.
const lastSearch = { term: null, data: null }

// Con una sola letra las sugerencias no acotan nada y sí cuestan una consulta.
const MIN_TERM_LENGTH = 2

export default class extends Controller {
  static targets = ['input', 'results']
  static values = { url: String, tipo: String, zona: String, claseEtf: String }

  connect() {
    this.selectedIndex = -1
    this.debounceTimer = null
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)
    this.restoreResults()
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
  }

  search() {
    const term = this.inputTarget.value.trim()
    if (term.length < MIN_TERM_LENGTH) {
      this.hideResults()
      return
    }

    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(term), 300)
  }

  // Tras el swap del turbo-frame el campo llega con el mismo texto: se repinta el
  // desplegable con el último resultado, o se vuelve a pedir si la respuesta
  // anterior se perdió en el repintado. Solo con el foco puesto en el campo, para
  // que entrar por URL con `?q=` no abra el desplegable sin que nadie lo pida.
  restoreResults() {
    if (document.activeElement !== this.inputTarget) return

    const term = this.inputTarget.value.trim()
    if (term.length < MIN_TERM_LENGTH) return

    if (term === lastSearch.term && lastSearch.data) {
      this.renderResults(lastSearch.data, term)
    } else {
      this.search()
    }
  }

  fetchResults(term) {
    let url = `${this.urlValue}?term=${encodeURIComponent(term)}`
    if (this.hasTipoValue && this.tipoValue) {
      url += `&tipo=${encodeURIComponent(this.tipoValue)}`
    }
    if (this.hasZonaValue && this.zonaValue) {
      url += `&zona=${encodeURIComponent(this.zonaValue)}`
    }
    if (this.hasClaseEtfValue && this.claseEtfValue) {
      url += `&clase_etf=${encodeURIComponent(this.claseEtfValue)}`
    }

    fetch(url, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(response => response.json())
      .then(data => this.renderResults(data, term))
      .catch(() => this.hideResults())
  }

  renderResults(data, term) {
    this.selectedIndex = -1
    lastSearch.term = term
    lastSearch.data = data

    if (!data || data.length === 0) {
      const msg = `No se encontraron resultados para "${this.escapeHtml(term)}"`
      this.resultsTarget.innerHTML = `<div class="rnk-AssetSearch_NoResults">${msg}</div>`
      this.resultsTarget.classList.add('rnk-AssetSearch_Results--visible')
      return
    }

    const items = data.map((item, index) => {
      const ticker = item.ticker ? `<span class="rnk-AssetSearch_Ticker">${this.escapeHtml(item.ticker)}</span>` : ''
      return `<a href="${item.url}"
                 class="rnk-AssetSearch_ResultItem"
                 data-index="${index}"
                 data-action="mouseenter->asset-search#highlightResult">
                ${ticker}
                <span class="rnk-AssetSearch_Name">${this.escapeHtml(item.name)}</span>
                <span class="rnk-AssetSearch_Type">${this.escapeHtml(item.type)}</span>
              </a>`
    }).join('')

    this.resultsTarget.innerHTML = items
    this.resultsTarget.classList.add('rnk-AssetSearch_Results--visible')
  }

  keydown(event) {
    const items = this.resultsTarget.querySelectorAll('.rnk-AssetSearch_ResultItem')
    if (!items.length) return

    switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
      this.updateHighlight(items)
      break
    case 'ArrowUp':
      event.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.updateHighlight(items)
      break
    case 'Enter':
      event.preventDefault()
      if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
        window.location.href = items[this.selectedIndex].getAttribute('href')
      }
      break
    case 'Escape':
      this.hideResults()
      this.inputTarget.blur()
      break
    }
  }

  highlightResult(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (!isNaN(index)) {
      this.selectedIndex = index
      const items = this.resultsTarget.querySelectorAll('.rnk-AssetSearch_ResultItem')
      this.updateHighlight(items)
    }
  }

  updateHighlight(items) {
    items.forEach((item, i) => {
      item.classList.toggle('rnk-AssetSearch_ResultItem--active', i === this.selectedIndex)
    })
  }

  // Cerrar el desplegable (escape, clic fuera, campo por debajo del mínimo) también
  // olvida el último resultado: si no, el siguiente repintado del frame lo volvería
  // a abrir solo.
  hideResults() {
    this.selectedIndex = -1
    lastSearch.term = null
    lastSearch.data = null
    this.resultsTarget.innerHTML = ''
    this.resultsTarget.classList.remove('rnk-AssetSearch_Results--visible')
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideResults()
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}
